import React from 'react';
import { Box, Container, Grid2 as Grid, Stack, Typography } from '@mui/material';

import CardHeader from '../../Components/DesignSystem/CardHeader';
import BaseImage from '../../Components/DesignSystem/BaseImage';
import MenuActions from '../../Components/DesignSystem/MenuActions';
import Hidden from '../../Components/DesignSystem/Hidden';
import PostTagsBuilder from '../../Components/Posts/PostTagsBuilder';
import { CloudinaryConfig, cloudinaryImageUrl } from '../../Services/Cloudinary';
import { Categories } from '../../Utils/Types/Posts';
import { isFolder } from '../../Utils/Post';
import PostIndicators from '../../Components/Posts/Shared/PostIndicators';
import PostStatus from '../../Components/Posts/Shared/PostStatus';
import actions from '../../Utils/Post/actions';
import AuditsLink from '../../Components/Posts/AuditsModal/AuditsLink';

interface PostHeaderProps {
  post: any;
  space: any;
}

function PostHeader({ post, space }: PostHeaderProps) {
  const hasImage = !!post.picture?.s3_key;
  const backgroundColor = isFolder(post.type) ? 'primary.light' : 'secondary.light';

  return (
    <Box
      sx={{
        backgroundColor,
        paddingY: { xs: 4, md: 8 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid
            sx={{
              display: 'flex',
              justifyContent: isFolder(post.type) ? 'center' : 'top',
              flexDirection: 'column',
            }}
            size={{
              xs: 12,
              md: hasImage ? 7 : 9,
            }}
          >
            {!isFolder(post.type) && (
              <Box sx={{ mb: 3 }}>
                <CardHeader
                  profilePictureUrl={cloudinaryImageUrl(
                    post.included.author?.picture?.s3_key,
                    CloudinaryConfig.AVATAR_48,
                    post.included.author?.picture?.title,
                  )}
                  fullName={post.included.author.full_name}
                  initials={post.included.author.initials}
                  date={post.published_at}
                  extra={<AuditsLink post={post} />}
                  postId={post.id}
                  alt="author avatar"
                  action={<MenuActions post={post} key={post.id} {...actions(post, space)} />}
                />
              </Box>
            )}

            <Stack direction="column-reverse">
              {/* that extra Stack is here to have the h1 before the h2 (WCAG purpose) */}
              <Box sx={{ mb: 3 }}>
                {isFolder(post.type) && (
                  <Box sx={{ float: 'right' }}>
                    <MenuActions post={post} key={post.id} {...actions(post, space)} />
                  </Box>
                )}
                <Typography component="h1" variant="h4">
                  {post.title}
                </Typography>
              </Box>
            </Stack>

            {post.problematic && (
              <Box sx={{ mb: 3 }}>
                <Typography component="h2" variant="h6">
                  {post.problematic}
                </Typography>
              </Box>
            )}

            {!isFolder(post.type) && <PostTagsBuilder post={post} space={space} />}

            <Hidden breakpoint="sm" direction="up">
              {[...Categories.PARTICIPATION, ...Categories.MESSAGING].includes(post.type) && (
                <Box sx={{ mt: 1, mb: 3 }}>
                  <PostStatus post={post} />
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <PostIndicators postContributionsStats={post.post_contributions_stats} />
              </Box>
            </Hidden>
          </Grid>

          {post.picture?.s3_key && (
            <Grid
              container
              sx={{
                alignItems: 'center',
              }}
              size={{
                xs: 12,
                md: hasImage ? 5 : 3,
              }}
            >
              <BaseImage
                rounded
                src={cloudinaryImageUrl(
                  post.picture.s3_key,
                  'w_460,h_308,q_auto,c_fill,g_auto',
                  post.picture.title,
                )}
                ratio="3:2"
                alt="post cover"
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

export default PostHeader;
